import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { DetailItem, WebsiteContent } from '../types/content';
import { appConfig } from '../config/appConfig';

interface ContentContextValue {
  data: WebsiteContent | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  getItem: (id: string) => DetailItem | undefined;
}

const ContentContext = createContext<ContentContextValue | null>(null);

export function ContentProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<WebsiteContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), appConfig.fetchTimeoutMs);

    try {
      const res = await fetch(appConfig.contentJsonPath, {
        signal: controller.signal,
        cache: 'default',
      });
      if (!res.ok) throw new Error(`Failed to load content (${res.status})`);
      const json = (await res.json()) as WebsiteContent;
      if (!json.highLevelIssues?.length || !json.index?.byId) {
        throw new Error('Content file is missing highLevelIssues or index.byId (schema v2 required)');
      }
      setData(json);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unknown error loading content';
      setError(message);
      setData(null);
    } finally {
      clearTimeout(timeout);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const getItem = useCallback(
    (id: string) => (data ? data.index.byId[id] : undefined),
    [data],
  );

  const value = useMemo(
    () => ({ data, loading, error, reload: load, getItem }),
    [data, loading, error, load, getItem],
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error('useContent must be used within ContentProvider');
  return ctx;
}
